import { useCallback, useContext, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Context } from '../../../context/AuthContext'
import PermissionComponent, { usePode } from '../../../permissions'
import { PERMISSOES } from '../../../const/permissoes'
import PageHeader from '../../Shared/PageHeader'
import ConfirmDialog from '../../Shared/ConfirmDialog'
import LoadingBackdrop from '../../Shared/LoadingBackdrop'
import SelectSubCompany from '../../Shared/SelectSubCompany'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import {
  createProcess,
  createProcessSequence,
  deleteProcess,
  deleteProcessSequence,
  editProcess,
  editProcessSequence,
  getProcess,
  reorderProcessSequence,
} from '../../../services/processServices'
import { mensagemDeErro } from '../../../services/apiClient'

const emptyFlowForm = { name: '', active: true }

// Sem `order`: a sequência é da API — nasce no fim (append) e muda pelas setas
// (reorderProcessSequence). O antigo campo "Ordem" só enviava um valor que a API
// ignorava, e a API renomeou `order` -> `sequence`.
const emptyStageForm = { name: '', active: true }

const ProcessFlow = () => {
  const { subCompany } = useContext(Context)
  const { notify, FeedbackSnackbar } = useFeedback()
  const podeGerenciar = usePode(PERMISSOES.FLUXO_GERENCIAR)

  const [subCompanyId, setSubCompanyId] = useState(
    subCompany.length === 1 ? subCompany[0].subCompanyId : ''
  )

  const [flows, setFlows] = useState([])
  const [selectedFlowId, setSelectedFlowId] = useState('')

  const [loadingFlows, setLoadingFlows] = useState(false)
  const [saving, setSaving] = useState(false)

  const [flowDialogOpen, setFlowDialogOpen] = useState(false)
  const [flowForm, setFlowForm] = useState(emptyFlowForm)
  const [editingFlow, setEditingFlow] = useState(null)
  const [flowToDelete, setFlowToDelete] = useState(null)

  const [stageDialogOpen, setStageDialogOpen] = useState(false)
  const [stageForm, setStageForm] = useState(emptyStageForm)
  const [editingStage, setEditingStage] = useState(null)
  const [stageToDelete, setStageToDelete] = useState(null)

  const fetchFlows = useCallback(async () => {
    if (!subCompanyId) {
      setFlows([])
      return
    }
    setLoadingFlows(true)
    try {
      const response = await getProcess({ subCompanyId })
      setFlows(response.data.result || [])
    } catch (error) {
      setFlows([])
      notify(mensagemDeErro(error, 'Não foi possível carregar os fluxos de produção.'), 'error')
    } finally {
      setLoadingFlows(false)
    }
  }, [subCompanyId, notify])

  useEffect(() => {
    setSelectedFlowId('')
    fetchFlows()
  }, [fetchFlows])

  const selectedFlow = flows.find(flow => flow.id === selectedFlowId) || null
  const stages = (selectedFlow && selectedFlow.stages) || []

  const updateFlowForm = (field, value) => setFlowForm(current => ({ ...current, [field]: value }))
  const updateStageForm = (field, value) =>
    setStageForm(current => ({ ...current, [field]: value }))

  // -------------------------------------------------------------- Fluxos --

  const handleNewFlow = () => {
    setEditingFlow(null)
    setFlowForm(emptyFlowForm)
    setFlowDialogOpen(true)
  }

  const handleEditFlow = flow => {
    setEditingFlow(flow)
    setFlowForm({ name: flow.name || '', active: flow.active !== false })
    setFlowDialogOpen(true)
  }

  const handleSaveFlow = async () => {
    if (!flowForm.name.trim()) {
      notify('Informe o nome do fluxo.', 'warning')
      return
    }
    setSaving(true)
    try {
      if (editingFlow) {
        await editProcess({ ...flowForm, processId: editingFlow.id })
        notify('Fluxo atualizado com sucesso!')
      } else {
        await createProcess({ ...flowForm, subCompanyId })
        notify('Fluxo criado com sucesso!')
      }
      setFlowDialogOpen(false)
      fetchFlows()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível salvar o fluxo.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteFlow = async () => {
    setSaving(true)
    try {
      await deleteProcess(flowToDelete.id)
      notify('Fluxo excluído com sucesso!')
      if (selectedFlowId === flowToDelete.id) setSelectedFlowId('')
      setFlowToDelete(null)
      fetchFlows()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível excluir o fluxo.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  // -------------------------------------------------------------- Etapas --

  const handleNewStage = () => {
    setEditingStage(null)
    setStageForm({ ...emptyStageForm })
    setStageDialogOpen(true)
  }

  const handleEditStage = stage => {
    setEditingStage(stage)
    setStageForm({
      name: stage.name || '',
      active: stage.active !== false,
    })
    setStageDialogOpen(true)
  }

  const handleSaveStage = async () => {
    if (!stageForm.name.trim()) {
      notify('Informe o nome da etapa.', 'warning')
      return
    }
    setSaving(true)
    try {
      const payload = {
        name: stageForm.name.trim(),
        active: stageForm.active,
        processId: selectedFlow.id,
      }
      if (editingStage) {
        await editProcessSequence({ ...payload, processSequenceId: editingStage.id })
        notify('Etapa atualizada com sucesso!')
      } else {
        await createProcessSequence(payload)
        notify('Etapa criada com sucesso!')
      }
      setStageDialogOpen(false)
      fetchFlows()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível salvar a etapa.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteStage = async () => {
    setSaving(true)
    try {
      await deleteProcessSequence(selectedFlow.id, stageToDelete.id)
      notify('Etapa excluída com sucesso!')
      setStageToDelete(null)
      fetchFlows()
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível excluir a etapa.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleMoveStage = async (index, direction) => {
    const target = index + direction
    if (target < 0 || target >= stages.length) return

    const reordered = [...stages]
    const [moved] = reordered.splice(index, 1)
    reordered.splice(target, 0, moved)

    setSaving(true)
    try {
      await reorderProcessSequence(
        selectedFlow.id,
        reordered.map(stage => stage.id)
      )
      notify('Ordem das etapas atualizada!')
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível salvar a nova ordem das etapas.'), 'error')
    } finally {
      setSaving(false)
      fetchFlows()
    }
  }

  return (
    <Box>
      <PageHeader
        title="Fluxos de Produção"
        subtitle="Configure as etapas do processo produtivo. As alterações refletem automaticamente no Kanban."
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <SelectSubCompany
              value={subCompanyId}
              onChange={event => setSubCompanyId(event.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {!subCompanyId ? (
        <Alert severity="info">Selecione uma filial para configurar os fluxos de produção.</Alert>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Fluxos
                </Typography>
                <PermissionComponent permissao={PERMISSOES.FLUXO_GERENCIAR}>
                  <Button size="small" startIcon={<AddIcon />} onClick={handleNewFlow}>
                    Novo fluxo
                  </Button>
                </PermissionComponent>
              </Box>
              <Divider />
              {flows.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                  {loadingFlows ? 'Carregando fluxos...' : 'Nenhum fluxo cadastrado nesta filial.'}
                </Typography>
              ) : (
                <List disablePadding>
                  {flows.map(flow => (
                    <ListItem
                      key={flow.id}
                      disablePadding
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip
                            title={podeGerenciar ? 'Editar' : 'Sem permissão para editar fluxos'}
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={!podeGerenciar}
                                onClick={() => handleEditFlow(flow)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={podeGerenciar ? 'Excluir' : 'Sem permissão para excluir fluxos'}
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={!podeGerenciar}
                                onClick={() => setFlowToDelete(flow)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      }
                    >
                      <ListItemButton
                        selected={selectedFlowId === flow.id}
                        onClick={() => setSelectedFlowId(flow.id)}
                        sx={{ borderRadius: 1, pr: 10 }}
                      >
                        <ListItemText
                          primary={flow.name || '—'}
                          secondary={flow.active === false ? 'Inativo' : null}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            {!selectedFlow ? (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  Selecione um fluxo para configurar suas etapas.
                </Typography>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    Etapas de {selectedFlow.name}
                  </Typography>
                  <PermissionComponent permissao={PERMISSOES.FLUXO_GERENCIAR}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleNewStage}
                    >
                      Nova etapa
                    </Button>
                  </PermissionComponent>
                </Box>
                <Divider />

                {stages.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    Nenhuma etapa configurada para este fluxo.
                  </Typography>
                ) : (
                  <List disablePadding sx={{ mt: 1 }}>
                    {stages.map((stage, index) => (
                      <ListItem
                        key={stage.id}
                        divider
                        secondaryAction={
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip
                              title={podeGerenciar ? 'Editar' : 'Sem permissão para editar etapas'}
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!podeGerenciar}
                                  onClick={() => handleEditStage(stage)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={podeGerenciar ? 'Excluir' : 'Sem permissão para excluir etapas'}
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={!podeGerenciar}
                                  onClick={() => setStageToDelete(stage)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        }
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
                          <Tooltip
                            title={
                              podeGerenciar
                                ? 'Mover para cima'
                                : 'Sem permissão para reordenar etapas'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={!podeGerenciar || index === 0}
                                onClick={() => handleMoveStage(index, -1)}
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              podeGerenciar
                                ? 'Mover para baixo'
                                : 'Sem permissão para reordenar etapas'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={!podeGerenciar || index === stages.length - 1}
                                onClick={() => handleMoveStage(index, 1)}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>

                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={500}>
                                {stage.name || '—'}
                              </Typography>
                              <Chip size="small" variant="outlined" label={`#${stage.sequence}`} />
                              {stage.active === false && (
                                <Chip size="small" color="default" label="Inativa" />
                              )}
                            </Box>
                          }
                          sx={{ pr: 10 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}

                <Alert severity="info" sx={{ mt: 2 }}>
                  As etapas configuradas aqui definem as colunas do Kanban de Produção. Cor,
                  obrigatoriedade, próxima etapa e permissões por etapa ainda não existem no banco
                  e por isso não são configuráveis.
                </Alert>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}

      <Dialog open={flowDialogOpen} onClose={() => setFlowDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingFlow ? 'Editar fluxo' : 'Novo fluxo'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Nome"
                value={flowForm.name}
                onChange={event => updateFlowForm('name', event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={flowForm.active}
                    onChange={event => updateFlowForm('active', event.target.checked)}
                  />
                }
                label="Ativo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setFlowDialogOpen(false)}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSaveFlow} disabled={saving}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={stageDialogOpen}
        onClose={() => setStageDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editingStage ? 'Editar etapa' : 'Nova etapa'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                required
                label="Nome"
                value={stageForm.name}
                onChange={event => updateStageForm('name', event.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={stageForm.active}
                    onChange={event => updateStageForm('active', event.target.checked)}
                  />
                }
                label="Ativa"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setStageDialogOpen(false)}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSaveStage} disabled={saving}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(flowToDelete)}
        title="Excluir fluxo"
        description="Tem certeza que deseja excluir esse fluxo e suas etapas?"
        confirmLabel="Excluir"
        loading={saving}
        onConfirm={handleDeleteFlow}
        onClose={() => setFlowToDelete(null)}
      />

      <ConfirmDialog
        open={Boolean(stageToDelete)}
        title="Excluir etapa"
        description="Tem certeza que deseja excluir essa etapa?"
        confirmLabel="Excluir"
        loading={saving}
        onConfirm={handleDeleteStage}
        onClose={() => setStageToDelete(null)}
      />

      <LoadingBackdrop open={saving} message="Processando..." />
      <FeedbackSnackbar />
    </Box>
  )
}

export default ProcessFlow
